import React, { lazy, Suspense, useEffect, useRef, useState } from "react";
import Styles from "./style.module.scss";

const InteractiveMap = lazy(() =>
  import("./InteractiveMap").then(({ InteractiveMap: MapComponent }) => ({ default: MapComponent }))
);

export const Map: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  const isPrerendering = Boolean(window.__PRERENDERING__);

  useEffect(() => {
    if (!apiKey || isPrerendering || shouldLoadMap || !containerRef.current) return undefined;

    if (!("IntersectionObserver" in window)) {
      setShouldLoadMap(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        setShouldLoadMap(true);
        observer.disconnect();
      },
      { rootMargin: "300px 0px" }
    );

    observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, [apiKey, isPrerendering, shouldLoadMap]);

  const fallback = (
    <div className={Styles.container__fallback} role="img" aria-label="Charlotte photography service area">
      Charlotte service-area map
    </div>
  );

  return (
    <div className={Styles.container} ref={containerRef}>
      {apiKey && !isPrerendering && shouldLoadMap ? (
        <Suspense fallback={fallback}>
          <InteractiveMap apiKey={apiKey} />
        </Suspense>
      ) : (
        fallback
      )}
      <a
        className={Styles.container__link}
        href="https://www.google.com/maps/place/Charlotte,+NC/"
        target="_blank"
        rel="noopener noreferrer"
      >
        Open in Google Maps <span aria-hidden="true">↗</span>
      </a>
    </div>
  );
};
